import { useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { PageHeader, Loading, ErrorState } from "@/components/common";
import { roleLabel } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, KeyRound, Loader2, UserCog, Trash2 } from "@/components/icons";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

const INPUT = "w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40";
const LABEL = "block text-sm font-medium text-slate-700 mb-1.5";
const ROLES = [
  { v: "seller", l: "Vendedor" }, { v: "manager", l: "Gerente" },
  { v: "director", l: "Diretor" }, { v: "diretor_geral", l: "Diretor Geral" },
  { v: "admin", l: "Administrador" },
  { v: "logistica_adm", l: "Adm Logístico" }, { v: "tv_corporativa", l: "TV Corporativa" },
];

export default function Ti() {
  const { user: current } = useAuth();
  const isAdmin = current?.role === "admin";
  const [showDemo, setShowDemo] = useState(false);
  const { data, loading, error, reload } = useFetch(`/ti/users${showDemo ? "?include_demo=true" : ""}`);
  const [openNew, setOpenNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [form, setForm] = useState({ name: "", email: "", role: "seller", manager_email: "", director_email: "", goal: 0, phone: "", funcoes_extras: [] });
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "seller", manager_email: "", director_email: "", phone: "", funcoes_extras: [] });

  const users = data || [];
  const managerOpts = users.filter((u) => ["admin", "manager"].includes(u.role) || (u.funcoes_extras || []).includes("manager"));
  const directorOpts = users.filter((u) => ["admin", "director"].includes(u.role));

  // Papéis extras válidos conforme o papel principal — sincronizado com o
  // backend (normalize_funcoes_extras): director pode acumular manager/seller,
  // manager pode acumular seller, os demais não acumulam.
  const extrasFor = (role) => role === "director" ? [{ v: "manager", l: "Gerente" }, { v: "seller", l: "Vendedor" }]
    : role === "manager" ? [{ v: "seller", l: "Vendedor" }] : [];
  const toggleExtra = (setter, v) => setter((f) => {
    const has = (f.funcoes_extras || []).includes(v);
    return { ...f, funcoes_extras: has ? f.funcoes_extras.filter((x) => x !== v) : [...(f.funcoes_extras || []), v] };
  });

  const openEdit = (u) => {
    const mgr = users.find((x) => x.id === u.manager_id);
    const dir = users.find((x) => x.id === u.director_id);
    setEditForm({ name: u.name || "", email: u.email || "", role: u.role, manager_email: mgr?.email || "", director_email: dir?.email || "", phone: u.phone || "", funcoes_extras: u.funcoes_extras || [] });
    setEditFor(u);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const payload = { name: editForm.name.trim(), email: editForm.email.trim(), role: editForm.role, phone: editForm.phone.trim() };
    if (editForm.role === "seller") {
      if (!editForm.manager_email) { toast.error("Selecione o gerente deste vendedor"); return; }
      payload.manager_email = editForm.manager_email;
    }
    if (editForm.role === "manager" && editForm.director_email) {
      payload.director_email = editForm.director_email;
    }
    // Sempre envia o array (mesmo vazio) para permitir limpar as funções.
    payload.funcoes_extras = editForm.funcoes_extras || [];
    setSaving(true);
    try {
      await api.patch(`/ti/users/${editFor.id}`, payload);
      toast.success("Perfil atualizado");
      setEditFor(null);
      reload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const doToggleAtivacao = async (u) => {
    setSaving(true);
    try {
      const novo = !u.is_active;
      await api.patch(`/ti/users/${u.id}/ativacao`, { is_active: novo });
      toast.success(novo ? "Usuário reativado" : "Usuário inativado (acesso bloqueado; dados preservados)");
      reload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Sanitiza payload: campos opcionais em branco viram undefined para não quebrar
      // a validação EmailStr do Pydantic (que recusa string vazia).
      const payload = {
        ...form,
        manager_email: form.manager_email?.trim() || undefined,
        director_email: form.director_email?.trim() || undefined,
        phone: form.phone?.trim() || undefined,
        funcoes_extras: form.funcoes_extras || [],
      };
      const { data: res } = await api.post("/ti/users", payload);
      toast.success(res.initial_password ? `Usuário criado. Senha inicial: ${res.initial_password}` : "Usuário criado");
      setOpenNew(false);
      setForm({ name: "", email: "", role: "seller", manager_email: "", director_email: "", goal: 0, phone: "", funcoes_extras: [] });
      reload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/ti/users/${u.id}`, { is_active: !u.is_active }); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const doReset = async () => {
    setSaving(true);
    try { await api.post(`/ti/users/${resetFor.id}/reset-password`, { new_password: newPw }); toast.success("Senha redefinida"); setResetFor(null); setNewPw(""); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={reload} message="Acesso restrito a Admin/TI." />;

  return (
    <div>
      <PageHeader title="Usuários" subtitle={`${(data || []).length === 1 ? "1 usuário" : `${(data || []).length} usuários`}`} action={
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-600 select-none cursor-pointer" data-testid="toggle-show-demo">
            <input type="checkbox" checked={showDemo} onChange={(e) => setShowDemo(e.target.checked)} className="rounded" />
            Mostrar dados de teste
          </label>
          <button onClick={() => setOpenNew(true)} data-testid="new-user-btn" className="flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover">
            <Plus className="w-4 h-4" /> Novo usuário
          </button>
        </div>
      } />

      <div className="bg-card rounded-2xl border border-black/5 overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.03)] divide-y divide-black/5" data-testid="users-list">
        {(data || []).map((u) => (
          <div key={u.id} className={"p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 " + (u.is_demo ? "bg-amber-50/40" : "")} data-testid={`user-row-${u.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-navy break-words flex items-center gap-2">
                {u.name}
                {u.is_demo && <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Teste</span>}
              </div>
              <div className="text-xs text-muted-foreground break-words">{u.email}</div>
              {u.phone ? (
                <div className="text-xs text-slate-400 mt-0.5">{u.phone}</div>
              ) : (
                <div className="text-xs text-warn font-medium mt-0.5" data-testid={`no-phone-${u.id}`}>sem telefone</div>
              )}
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                  {u.role_label || roleLabel(u.role)}
                </span>
                {(u.funcoes_extras || []).length > 0 && (
                  <span data-testid={`user-funcoes-${u.id}`} className="inline-flex flex-wrap items-center gap-1">
                    {(u.funcoes_extras || []).map((f) => (
                      <span key={f} className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-brand-soft text-brand-hover">
                        +{f === "manager" ? "Gerente" : f === "seller" ? "Vendedor" : f}
                      </span>
                    ))}
                  </span>
                )}
                {(u.manager_name || u.director_name) && (
                  <span className="text-[11px] text-slate-500">· {u.manager_name || u.director_name}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap md:flex-nowrap justify-start md:justify-end">
              <button onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}
                className={`text-xs font-medium px-2.5 py-1 rounded-full ${u.is_active ? "bg-ok-soft text-ok" : "bg-slate-100 text-slate-500"}`}>
                {u.is_active ? "Ativo" : "Inativo"}
              </button>
              {u.role !== "admin" && (
                <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-brand-soft text-brand-hover text-xs font-semibold hover:brightness-95"
                  title="Editar perfil, papel e funções">
                  <UserCog className="w-4 h-4" /> Editar
                </button>
              )}
              <button onClick={() => setResetFor(u)} data-testid={`reset-pw-${u.id}`}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-input text-slate-600 text-xs font-medium hover:bg-slate-50"
                title="Redefinir senha">
                <KeyRound className="w-4 h-4" /> <span className="hidden sm:inline">Senha</span>
              </button>
              {isAdmin && u.role !== "admin" && u.id !== current?.id && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`toggle-ativacao-${u.id}`}
                      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-medium ${u.is_active ? "text-bad hover:bg-bad-soft" : "text-ok hover:bg-ok-soft"}`}
                      title={u.is_active ? "Inativar usuário" : "Reativar usuário"}>
                      <Trash2 className="w-4 h-4" /> <span className="hidden sm:inline">{u.is_active ? "Inativar" : "Reativar"}</span>
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{u.is_active ? `Inativar ${u.name}?` : `Reativar ${u.name}?`}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {u.is_active
                          ? "O usuário perde o acesso ao sistema imediatamente. Nenhum dado é apagado: clientes, oportunidades, atividades, histórico e vínculos continuam no banco e visíveis para o gestor. Dá para reativar depois."
                          : "O usuário recupera o acesso ao sistema com as mesmas permissões de antes."}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction data-testid={`confirm-toggle-ativacao-${u.id}`} onClick={() => doToggleAtivacao(u)}
                        className={u.is_active ? "bg-bad text-white hover:bg-bad/90" : "bg-ok text-white hover:bg-ok/90"}>
                        {u.is_active ? "Inativar" : "Reativar"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo usuário</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div><label className={LABEL}>Nome</label><input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={INPUT} data-testid="new-user-name" /></div>
            <div><label className={LABEL}>E-mail</label><input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={INPUT} data-testid="new-user-email" /></div>
            <div><label className={LABEL}>Telefone (para recuperação de senha por SMS)</label><input type="tel" placeholder="+55 11 99999-9999" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={INPUT} data-testid="new-user-phone" inputMode="tel" /></div>
            <div><label className={LABEL}>Papel</label>
              <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className={INPUT} data-testid="new-user-role">
                {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </div>
            {form.role === "seller" && <div><label className={LABEL}>E-mail do gerente</label><input required type="email" value={form.manager_email} onChange={(e) => setForm((f) => ({ ...f, manager_email: e.target.value }))} className={INPUT} /></div>}
            {form.role === "manager" && <div><label className={LABEL}>E-mail do diretor (opcional)</label><input type="email" value={form.director_email} onChange={(e) => setForm((f) => ({ ...f, director_email: e.target.value }))} className={INPUT} /></div>}
            {extrasFor(form.role).length > 0 && (
              <div>
                <label className={LABEL}>Funções adicionais (acumulação de papel)</label>
                <div className="flex flex-wrap gap-2" data-testid="new-user-extras">
                  {extrasFor(form.role).map((op) => {
                    const on = (form.funcoes_extras || []).includes(op.v);
                    return (
                      <button type="button" key={op.v} onClick={() => toggleExtra(setForm, op.v)}
                        data-testid={`new-user-extra-${op.v}`}
                        className={`text-xs font-medium px-2.5 py-1.5 rounded-full border ${on ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-input hover:bg-slate-50"}`}>
                        {on ? "✓ " : "+ "}Também atua como {op.l}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">Diretores gerais mantêm apenas a visão macro — não acumulam papel.</p>
              </div>
            )}
            <DialogFooter>
              <button type="submit" disabled={saving} data-testid="save-user-btn" className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover flex items-center gap-2 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Criar
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar perfil · {editFor?.name}</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <div className="text-xs text-muted-foreground -mt-1">ID: {editFor?.id}</div>
            <div><label className={LABEL}>Nome</label><input required value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className={INPUT} data-testid="edit-user-name" /></div>
            <div><label className={LABEL}>E-mail</label><input required type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} className={INPUT} data-testid="edit-user-email" /></div>
            <div><label className={LABEL}>Telefone (recuperação de senha por SMS)</label><input type="tel" placeholder="+55 11 99999-9999" value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} className={INPUT} data-testid="edit-user-phone" inputMode="tel" /></div>
            <div><label className={LABEL}>Papel</label>
              <select value={editForm.role} onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))} className={INPUT} data-testid="edit-user-role">
                {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </div>
            {editForm.role === "seller" && (
              <div><label className={LABEL}>Gerente responsável</label>
                <select required value={editForm.manager_email} onChange={(e) => setEditForm((f) => ({ ...f, manager_email: e.target.value }))} className={INPUT} data-testid="edit-user-manager">
                  <option value="">Selecione um gerente…</option>
                  {managerOpts.map((m) => <option key={m.id} value={m.email}>{m.name} · {m.email}</option>)}
                </select>
              </div>
            )}
            {editForm.role === "manager" && (
              <div><label className={LABEL}>Diretor (opcional)</label>
                <select value={editForm.director_email} onChange={(e) => setEditForm((f) => ({ ...f, director_email: e.target.value }))} className={INPUT} data-testid="edit-user-director">
                  <option value="">Sem diretor vinculado</option>
                  {directorOpts.map((d) => <option key={d.id} value={d.email}>{d.name} · {d.email}</option>)}
                </select>
              </div>
            )}
            {extrasFor(editForm.role).length > 0 && (
              <div>
                <label className={LABEL}>Funções adicionais (acumulação de papel)</label>
                <div className="flex flex-wrap gap-2" data-testid="edit-user-extras">
                  {extrasFor(editForm.role).map((op) => {
                    const on = (editForm.funcoes_extras || []).includes(op.v);
                    return (
                      <button type="button" key={op.v} onClick={() => toggleExtra(setEditForm, op.v)}
                        data-testid={`edit-user-extra-${op.v}`}
                        className={`text-xs font-medium px-2.5 py-1.5 rounded-full border ${on ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-input hover:bg-slate-50"}`}>
                        {on ? "✓ " : "+ "}Também atua como {op.l}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  {editForm.role === "director"
                    ? "Um Diretor pode acumular Gerente e/ou Vendedor em regiões específicas — mantendo a diretoria principal."
                    : "Um Gerente pode acumular Vendedor para atender contas diretas."}
                </p>
              </div>
            )}
            {["director", "diretor_geral"].includes(editForm.role) && (
              <div className="text-xs text-muted-foreground bg-slate-50 border border-black/5 rounded-lg p-3">
                {editForm.role === "diretor_geral"
                  ? "Diretor Geral enxerga toda a empresa (todos os diretores, gerentes e vendedores)."
                  : "Diretor enxerga os gerentes e vendedores da sua diretoria."}
              </div>
            )}
            <DialogFooter>
              <button type="submit" disabled={saving} data-testid="save-edit-user-btn" className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover flex items-center gap-2 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salvar alterações
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Redefinir senha · {resetFor?.name}</DialogTitle></DialogHeader>
          <input type="text" placeholder="Nova senha (mín. 6)" value={newPw} onChange={(e) => setNewPw(e.target.value)} className={INPUT} data-testid="reset-pw-input" />
          <DialogFooter>
            <button onClick={doReset} disabled={saving || newPw.length < 6} data-testid="confirm-reset-pw" className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Redefinir
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
